import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Button,
  Group,
  Stack,
  Text,
  Alert,
  Tabs,
  Grid,
  Paper,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { Page, FormSectionCard } from "../layout/page";
import { useServerApi } from "../hooks/userServerApi";
import { useAuthUser } from "react-auth-kit";
import McaStatusBadge from "../components/mca/McaStatusBadge";
import McaTimeline from "../components/mca/McaTimeline";
import McaDataDiff from "../components/mca/McaDataDiff";
import McaStepDetail from "../components/mca/McaStepDetail";
import McaOverview from "../components/mca/McaOverview";
import McaAuditLogTable from "../components/mca/McaAuditLogTable";
import { getMcaRequestPermissions, isUserApproverForStep, getSessionUserIds } from "../components/mca/mcaRequestPermissions";
import { getEntityLabel, migrateStepFields, isFileFieldType } from "../data/mcaEntities";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { useTabActionsOptional } from "../context/tabContext";
import McaRejectionPanel from "../components/mca/McaRejectionPanel";
import { openEntityEditTab } from "../utils/openEntityEditTab";
import { refreshEntityTabForMcaRequest } from "../utils/refreshEntityTabForMcaRequest";

const MCA_REQUESTS_LIST_PATH = "/mca/requests";

const getDefaultSelectedStep = (request) => {
  const steps = request?.stepResults ?? [];
  if (!steps.length) return 0;
  if (["PENDING", "IN_PROGRESS"].includes(request?.status)) {
    return request.currentStep ?? 0;
  }
  if (request?.status === "REJECTED") {
    return request.lastRejection?.step ?? request.currentStep ?? 0;
  }
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].status !== "PENDING") return i;
  }
  return 0;
};

const McaRequestDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const tabNav = useTabNavigate();
  const tabActions = useTabActionsOptional();
  const [api] = useServerApi();
  const auth = useAuthUser();
  const session = auth();
  const [request, setRequest] = useState(null);
  const [revisedPayload, setRevisedPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  const [selectedStep, setSelectedStep] = useState(0);

  const load = async () => {
    const doc = await api.getById({ apiEntity: "mcaRequest", id });
    setRequest(doc);
    if (doc?.payload) {
      setRevisedPayload(doc.payload);
    }
    if (doc) {
      setSelectedStep(getDefaultSelectedStep(doc));
    }
    return doc;
  };

  useEffect(() => {
    load();
  }, [id]);

  const process = request?.process;
  const permissions = useMemo(
    () => (request ? getMcaRequestPermissions(request, session) : {}),
    [request, session]
  );
  const {
    isMaker,
    canAct,
    canApprove,
    isApprover,
    noApproversConfigured,
    userId,
  } = permissions;

  const sortedSteps = (process?.steps ?? [])
    .slice()
    .sort((a, b) => a.order - b.order);

  const canUploadOnStep = (stepIndex) => {
    const stepDef = sortedSteps[stepIndex];
    const fieldDefs = migrateStepFields(stepDef ?? {});
    const hasFileFields = fieldDefs.some((f) => isFileFieldType(f.inputType));
    if (!hasFileFields) return false;
    const { userRoleId } = getSessionUserIds(session);
    const approverForStep = isUserApproverForStep(userId, userRoleId, stepDef);
    if (!isMaker && !approverForStep) return false;
    if (request.status === "REJECTED" && isMaker) {
      return stepIndex === (request.lastRejection?.step ?? request.currentStep);
    }
    if (["PENDING", "IN_PROGRESS"].includes(request.status)) {
      return stepIndex === request.currentStep;
    }
    return false;
  };

  const handleUploadField = async (fieldIndex, file) => {
    setUploading(true);
    try {
      const res = await api.Mca.uploadStepFieldFile(id, fieldIndex, file);
      if (!res?.success) {
        throw new Error(res?.message || res?.error || "Upload failed");
      }
      await reload();
      showNotification({ title: "Uploaded", message: "File uploaded", color: "green" });
    } catch (e) {
      showNotification({ title: "Error", message: e.message, color: "red" });
      throw e;
    } finally {
      setUploading(false);
    }
  };

  const reload = async () => {
    const doc = await load();
    tabActions?.notifyListRefresh?.(MCA_REQUESTS_LIST_PATH);
    refreshEntityTabForMcaRequest(tabActions, {
      targetEntity: doc?.targetEntity,
      targetEntityId: doc?.targetEntityId,
      status: doc?.status,
      userRole: session?.userRole,
      currentPathname: location.pathname,
    });
  };

  if (!request) {
    return (
      <Page title="MCA Request">
        <Text color="dimmed">Loading...</Text>
      </Page>
    );
  }

  return (
    <Page
      title={
        <Group spacing="sm">
          <Text weight={700} size="xl">
            {request.code}
          </Text>
          <McaStatusBadge status={request.status} />
        </Group>
      }
      actions={
        <Group>
          {request.targetEntityId && (
            <Button
              size="sm"
              color="teal"
              onClick={() =>
                openEntityEditTab(tabNav, {
                  entity: request.targetEntity,
                  id: request.targetEntityId,
                  label:
                    request.targetEntityCode ??
                    getEntityLabel(request.targetEntity),
                  userRole: session?.userRole,
                  currentPathname: location.pathname,
                })
              }
            >
              View Entity
            </Button>
          )}
          <Button size="sm" variant="default" onClick={() => tabNav.closeActive()}>
            Close
          </Button>
        </Group>
      }
    >
      <Stack spacing="xl">
        <McaOverview request={request} />

        <FormSectionCard title="Approval Details">
          <Tabs defaultValue="approval" variant="outline">
            <Tabs.List>
              <Tabs.Tab value="approval">Approval</Tabs.Tab>
              <Tabs.Tab value="changes">Proposed Changes</Tabs.Tab>
              <Tabs.Tab value="logs">Audit Logs</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="approval" pt="md">
              <Stack spacing="md">
                {request.status === "REJECTED" && request.lastRejection && (
                  <McaRejectionPanel
                    request={request}
                    process={process}
                    isMaker={isMaker}
                    revisedPayload={revisedPayload}
                    onRevisedPayloadChange={setRevisedPayload}
                    resubmitting={resubmitting}
                    onResubmit={async ({ payload, comment }) => {
                      setResubmitting(true);
                      try {
                        const result = await api.Mca.resubmitRequest(id, {
                          payload,
                          comment,
                        });
                        if (!result?.success) {
                          const detail =
                            typeof result?.error === "string"
                              ? result.error
                              : result?.error?.message;
                          throw new Error(
                            detail ?? result?.message ?? "Resubmit failed"
                          );
                        }
                        const updated = result.data;
                        if (updated) {
                          setRequest(updated);
                          setRevisedPayload(updated.payload ?? payload);
                          setSelectedStep(getDefaultSelectedStep(updated));
                        }
                        showNotification({
                          title: "Resubmitted",
                          color: "green",
                        });
                        await reload();
                      } catch (e) {
                        showNotification({
                          title: "Error",
                          message: e.message,
                          color: "red",
                        });
                      } finally {
                        setResubmitting(false);
                      }
                    }}
                  />
                )}

                {canAct && isMaker && (
                  <Alert color="blue" title="Awaiting approval">
                    You submitted this request. Another user assigned as an
                    approver must review and approve it — you cannot approve
                    your own submission.
                  </Alert>
                )}

                {canAct && !isMaker && !isApprover && !noApproversConfigured && (
                  <Alert color="gray" title="View only">
                    You are not assigned as an approver for the current step.
                    Open <strong>My Pending Approvals</strong> to see requests
                    waiting on you.
                  </Alert>
                )}

                {noApproversConfigured && (
                  <Alert color="red" title="No approvers configured">
                    This process step has no approvers or approver roles. Edit
                    the approval process and assign approvers on the Approval
                    Steps tab.
                  </Alert>
                )}

                <Grid gutter="lg">
                  <Grid.Col span={12} md={4}>
                    <McaTimeline
                      request={request}
                      selectedStep={selectedStep}
                      onSelectStep={setSelectedStep}
                    />
                  </Grid.Col>
                  <Grid.Col span={12} md={8}>
                    <McaStepDetail
                      request={request}
                      process={process}
                      selectedStep={selectedStep}
                      canApprove={canApprove}
                      canUpload={canUploadOnStep(selectedStep)}
                      loading={loading}
                      uploading={uploading}
                      onUploadField={handleUploadField}
                      onApprove={async (body) => {
                        setLoading(true);
                        try {
                          const res = await api.Mca.approveStep(id, body);
                          if (!res?.success) {
                            const msg =
                              res?.error?.message ||
                              res?.message ||
                              (Array.isArray(res?.error?.missingStepFields)
                                ? `Missing input fields: ${res.error.missingStepFields.join(", ")}`
                                : "Approve failed");
                            throw new Error(msg);
                          }
                          await reload();
                        } finally {
                          setLoading(false);
                        }
                      }}
                      onReject={async (body) => {
                        setLoading(true);
                        try {
                          await api.Mca.rejectStep(id, body);
                          await reload();
                        } finally {
                          setLoading(false);
                        }
                      }}
                      onTerminate={async (body) => {
                        setLoading(true);
                        try {
                          await api.Mca.terminateRequest(id, {
                            reason: body.reason,
                          });
                          await reload();
                        } finally {
                          setLoading(false);
                        }
                      }}
                    />
                  </Grid.Col>
                </Grid>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="changes" pt="md">
              <Paper withBorder p="md" radius="sm">
                <McaDataDiff
                  action={request.action}
                  payloadDisplay={request.payloadDisplay}
                  targetEntity={request.targetEntity}
                  targetEntityId={request.targetEntityId}
                  targetEntityCode={request.targetEntityCode}
                  onViewEntity={
                    request.targetEntityId
                      ? () =>
                          openEntityEditTab(tabNav, {
                            entity: request.targetEntity,
                            id: request.targetEntityId,
                            label:
                              request.targetEntityCode ??
                              getEntityLabel(request.targetEntity),
                            userRole: session?.userRole,
                            currentPathname: location.pathname,
                          })
                      : undefined
                  }
                />
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="logs" pt="md">
              <McaAuditLogTable logs={request.logs} />
            </Tabs.Panel>
          </Tabs>
        </FormSectionCard>
      </Stack>
    </Page>
  );
};

export default McaRequestDetail;
